import { Pipe, PipeTransform, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

@Pipe({
  name: 'formatMessage',
  standalone: true,
})
export class FormatMessagePipe implements PipeTransform {
  constructor(private readonly sanitizer: DomSanitizer) {}

  transform(text: string): string {
    if (!text) {
      return '';
    }

    const decodedText = this.decodeHtmlEntities(text);

    const formattedText = this.containsHtml(decodedText)
      ? decodedText
      : this.convertPlainTextToHtml(decodedText);
    const sanitizedHtml =
      this.sanitizer.sanitize(SecurityContext.HTML, formattedText) ?? '';

    return this.decorateAnchors(sanitizedHtml);
  }

  private decodeHtmlEntities(text: string): string {
    if (!text.includes('&') || typeof document === 'undefined') {
      return text;
    }

    const textArea = document.createElement('textarea');
    textArea.innerHTML = text;
    return textArea.value;
  }

  private containsHtml(text: string): boolean {
    return /<\/?[a-z][\s\S]*>/i.test(text);
  }

  private convertPlainTextToHtml(text: string): string {
    const paragraphs = text.split(/\n\s*\n|\r\n\s*\r\n/);

    const processedParagraphs = paragraphs.map(paragraph => {
      if (paragraph.includes('\n- ') || paragraph.startsWith('- ')) {
        const lines = paragraph.split(/\r?\n/);
        const processedLines = lines.map(line => {
          if (line.trim().startsWith('- ')) {
            return `<li>${line.substring(2).trim()}</li>`;
          }

          return line;
        });

        const bulletPoints = processedLines.filter(line =>
          line.startsWith('<li>')
        );

        if (bulletPoints.length > 0) {
          let result = processedLines
            .filter(line => !line.startsWith('<li>') && line.trim().length > 0)
            .join('<br>');

          result += `<ul class="bullet-list">${bulletPoints.join('')}</ul>`;

          return result;
        }
      }

      return paragraph.replaceAll(/\r?\n/g, '<br>');
    });

    return processedParagraphs.join('<br><br>');
  }

  private decorateAnchors(html: string): string {
    if (typeof DOMParser === 'undefined') {
      return html;
    }

    const documentFragment = new DOMParser().parseFromString(html, 'text/html');

    this.normalizeWhitespace(documentFragment);

    documentFragment.querySelectorAll('a').forEach(anchor => {
      const href = anchor.getAttribute('href')?.trim() ?? '';

      if (href && !/^(https?:|mailto:|tel:|\/|#|\/\/)/i.test(href)) {
        anchor.setAttribute('href', `https://${href}`);
      }

      anchor.setAttribute('target', '_blank');
      anchor.setAttribute('rel', 'noopener noreferrer');
    });

    return documentFragment.body.innerHTML;
  }

  private normalizeWhitespace(documentFragment: Document): void {
    const walker = documentFragment.createTreeWalker(
      documentFragment.body,
      NodeFilter.SHOW_TEXT
    );

    let node = walker.nextNode();
    while (node) {
      const textNode = node as Text;
      if (!textNode.parentElement?.closest('pre, code')) {
        textNode.textContent = (textNode.textContent ?? '').replace(
          /\s+/g,
          ' '
        );
      }
      node = walker.nextNode();
    }
  }
}
